// client/src/components/layout/nav-items.ts
//
// Single source of truth for the primary app navigation. BOTH the desktop
// sidebar (sidebar.tsx) and the mobile bottom-tabs (bottom-tabs.tsx) build their
// entries from these functions, so the two surfaces can never drift to offer
// different options (the bug that prompted the unification: mobile previously
// hardcoded just "Security" + Profile while desktop showed the full
// context-aware list).
import {
  Shield,
  Key,
  KeyRound,
  LayoutDashboard,
  ScrollText,
  Inbox,
  Users2,
  Handshake,
  Building2,
  MailOpen,
  ToggleLeft,
  Activity,
} from "lucide-react";
import { usePendingInvitationCounts } from "@/lib/pending-invitations";
import { useFeatureFlagWithDefault } from "@/lib/use-feature-flag";
import { FLAG_KEYS } from "@shared/feature-flags";
import type { FlagKey } from "@shared/feature-flags";
import { PLATFORM_ADMIN_ROLE } from "@shared/lib/roles";
import { VAULT_PAGE_TITLES } from "@/lib/vault-titles";

export interface NavItem {
  icon: typeof Shield;
  label: string;
  /**
   * Shorter wording for the MOBILE bottom bar only (bottom-tabs.tsx), where
   * six platform tabs share a 320px phone. The sidebar always spells `label`
   * out. Omitted items show `label` on both surfaces, so the two navs still
   * carry the same DESTINATIONS item-for-item (the parity rule is about
   * hrefs, not wording — bottom-tabs.test.tsx asserts the href lists match).
   */
  barLabel?: string;
  href: string;
  disabled?: boolean;
  /**
   * Extra path prefixes that should also mark this item active. Used by
   * Security, which fronts the Memories + Guardians sections (now tabs under
   * it — see security-section-tabs.tsx), so the nav entry stays highlighted on
   * `/memories` and `/guardian-setup` too.
   */
  matchPrefixes?: string[];
  /**
   * Pilot-core flag gating this item's visibility (Task 27, #1407) — omitted
   * items always show. Mirrors (but does not replace) the authoritative
   * server 404 gate at the item's route registration.
   */
  flagKey?: FlagKey;
  /**
   * Minimum protection level the DESTINATION route enforces. Display-only —
   * the route's own Guarded wrapper remains the sole enforcement (AC-7,
   * #1791). Used to render a lock affordance BEFORE the click, never to gate
   * navigation itself.
   */
  minAal?: "AAL2" | "AAL3";
  /**
   * Attention count resolved by `useVisibleNavItems` (#2171). Set on the
   * builder's item as a KEY, not a number — the builders are plain functions
   * with no hook access, so the count is attached during the hook pass that
   * both navs already share. That keeps desktop/mobile parity structural
   * rather than something two renderers have to remember to do the same way.
   */
  badgeKey?: NavBadgeKey;
  /** Resolved count, attached by `useVisibleNavItems`. Never set by a builder. */
  badgeCount?: number;
}

/** The one attention counter the nav can render today. */
export type NavBadgeKey = "invitations";

/**
 * True when `currentAal` ranks below `minAal` — used ONLY to decide whether
 * to render the display-only lock affordance (AC-7). Mirrors the aalRank
 * comparison in app/guards.tsx's `Guarded` (the real enforcement); kept as a
 * small local rank map rather than importing guards.tsx, since Guarded's
 * rank table is a private implementation detail of the actual gate, not a
 * shared export — duplicating a 3-line lookup here is cheaper than coupling
 * this display-only helper to that module's internals.
 */
export function isBelowMinAal(
  currentAal: "AAL1" | "AAL2" | "AAL3" | undefined,
  minAal: "AAL2" | "AAL3" | undefined,
): boolean {
  if (!minAal) return false;
  const aalRank: Record<string, number> = { AAL1: 1, AAL2: 2, AAL3: 3 };
  return (aalRank[currentAal ?? ""] ?? 0) < aalRank[minAal];
}

// The five section items resolve to the SAME `/admin/platform/:tab?` route
// (admin-routes.tsx, Guarded minAal="AAL3") — "orgs", "access", "features"
// and "status" are tab values consumed by that one dynamic route, not
// separate route registrations. "All activity" is the exception: it is a
// DESTINATION with its own `/admin/platform/activity` <Route>, registered
// BEFORE the `:tab?` fallback so the fallback cannot swallow it. Audited
// against admin-routes.tsx directly, same as the personal/org items above
// (adversarial-review follow-up, #1791).
//
// `role` (Task 24, AC-32, #169) is OPTIONAL and additive-only for backward
// compatibility: every pre-existing no-arg caller (tests + both layout
// components before this task) keeps seeing the same items. When `role` IS
// supplied and is anything other than "admin-platform", the whole list is
// filtered to `[]` — a non-operator AAL3 user who reaches this builder (e.g.
// by navigating to the URL directly) gets no platform nav items rendered, the
// tightening half of AC-32. This is a link-VISIBILITY gate only; the real
// authorization boundary stays server-side (`requirePlatformAdmin`) and the
// pre-existing route-ACCESS gap (route-context.ts:69-72) is unchanged by this
// task (named boundary, spec §1).
export function buildPlatformNavItems(role?: string): NavItem[] {
  if (role !== undefined && role !== PLATFORM_ADMIN_ROLE) return [];
  return [
    { icon: LayoutDashboard, label: "Home", href: "/admin/platform", minAal: "AAL3" },
    { icon: Building2, label: "Organisations", barLabel: "Orgs", href: "/admin/platform/orgs",
      matchPrefixes: ["/admin/platform/orgs", "/admin/platform/org/"], minAal: "AAL3" },
    { icon: KeyRound, label: "Platform access", barLabel: "Access", href: "/admin/platform/access", minAal: "AAL3" },
    { icon: ToggleLeft, label: "Features", href: "/admin/platform/features", minAal: "AAL3" },
    { icon: Activity, label: "System status", barLabel: "Status", href: "/admin/platform/status", minAal: "AAL3" },
    // Activity is a destination, not a tab: it is also reached from Home, from
    // an organisation and from a person, always pre-filtered. It stays in the
    // nav so a phone can reach it without finding an inline link first (§2.2
    // parity — the footer carve-out covers the profile block only).
    { icon: ScrollText, label: "All activity", barLabel: "Activity", href: "/admin/platform/activity", minAal: "AAL3" },
  ];
}

export function buildNavItems(currentContext: "personal" | "org"): NavItem[] {
  // "Home" — matches the org hub's own first tab id/label (`ORG_TAB_IDS`,
  // `dashboard-tab-bar.tsx`) since the org-dashboard-redesign renamed it
  // from "Overview" (final review M-5: this label had drifted from the tab
  // it lands on, two names for one destination in adjacent chrome). The
  // destination is a KPI/metrics dashboard, not the org's profile/settings
  // — AC-8, #1791.
  // App.tsx:181 (DashboardRoute's own <Guarded minAal="AAL2">) — /dashboard
  // doesn't wrap <Guarded> directly at its <Route>, it renders DashboardRoute,
  // which does. Audited against App.tsx directly (AC-7 follow-up, Block 1).
  const dashboard: NavItem = { icon: LayoutDashboard, label: "Home", href: "/dashboard", minAal: "AAL2" };
  // Security fronts the personal identity surfaces: the protection overview
  // (this route), Security Keys, Memories, and Guardians, presented as
  // sibling entries within the section rather than separate top-level nav
  // items (security-section-tabs.tsx).
  const security: NavItem = {
    icon: Shield,
    label: "Security",
    href: "/security-centre",
    matchPrefixes: ["/security-centre", "/security-keys", "/memories", "/guardian-setup"],
  };
  // App.tsx:713 — Guarded minAal="AAL2".
  const connections: NavItem = { icon: Users2, label: "Connections", href: "/connections", minAal: "AAL2" };
  // Invitations (#2171) — every decision waiting on this PERSON: an org asking
  // them to hold a role inside it, an org asking to connect, or another person
  // asking them to be their guardian. Deliberately NOT folded into
  // Connections: Connections is "who am I already connected to", and an
  // unanswered question buried above that list was only discoverable by
  // accident. The badge is the discovery mechanism the issue calls for — a
  // plain authenticated read, so it survives a reload and needs no live
  // socket, unlike the WebSocket notice which reaches a live session only.
  //
  // PERSONAL CONTEXT ONLY, and that is a touch-target constraint rather than a
  // preference: the org list already holds 6 items, and every item is a direct
  // mobile tab (no overflow) — a 7th would put 8 buttons across a 320px phone
  // at 40px each, under the 44px minimum (DESIGN-SYSTEM.md §3.1, pinned by
  // bottom-tabs.test.tsx's item-count ceiling). Being invited is a decision
  // about you as a person, never about the org you are acting for, so personal
  // is also its correct home. In ORG context the standing signal is the badge
  // on the org switcher's "Personal" row (options-list.tsx) — the row someone
  // would switch through to get here — plus the pointer on the Connections
  // page and the live toast. There is deliberately NO org-context nav badge,
  // and no doc or copy may imply one.
  const invitations: NavItem = {
    icon: MailOpen,
    label: "Invitations",
    href: "/invitations",
    minAal: "AAL2",
    badgeKey: "invitations",
  };
  // Inbox (personal) — the conversation-first front door (#1290 / #1291): clicking
  // Inbox lands on the Conversations list, NOT the Received tab. Received/Sent stay
  // reachable via the in-page InboxNav switcher (Received still resolves /challenges).
  // Org context uses the issued list instead, set inline in the org branch below.
  // Gated on comms_hub (Task 27, #1407) — the conversation view is the comms-hub
  // surface; a payment_requests-only shutoff does not hide this entry (the
  // in-page views empty-state their own gated tabs).
  // App.tsx:607 — Guarded minAal="AAL2".
  const challenges: NavItem = {
    icon: Inbox,
    label: "Inbox",
    href: "/challenges/conversations",
    flagKey: FLAG_KEYS.COMMS_HUB,
    minAal: "AAL2",
    // Task 7 (approval-workflows-in-communications-hub #module 04, AC-13):
    // Approvals is now a tab INSIDE the Conversations page, reached at
    // /challenges/workflows(/...) — fold its route into the Inbox nav
    // highlight instead of adding a second sidebar entry (mirrors `security`'s
    // matchPrefixes pattern above).
    matchPrefixes: ["/challenges/conversations", "/challenges/workflows"],
  };
  // App.tsx:664 — Guarded minAal="AAL2".
  const audit: NavItem = { icon: ScrollText, label: "Activity", href: "/audit", minAal: "AAL2" };

  // Security is a personal-only surface (hardware key + personal sensory
  // seeds + personal guardians) — intentionally omitted from the org view.
  // Personal context intentionally omits Dashboard — the dashboard is an
  // org-scoped surface (KPIs, members, org audit). Personal users get
  // Security as their home and Activity for their own audit trail. Memories and
  // Guardians are no longer separate entries — they live as tabs inside the
  // Security section (security-section-tabs.tsx).
  const items: NavItem[] =
    currentContext === "org"
      ? [
          dashboard,
          connections,
          { icon: Key, label: VAULT_PAGE_TITLES.org, href: "/org-vault", flagKey: FLAG_KEYS.BANK_ACCOUNTS, minAal: "AAL2" },
          // Org-context Inbox now lands on the SAME conversation-first list personal
          // context uses (AC-28, #1715 Module 04 Task 6 — owner-reversible decision,
          // flagged in the PR) rather than the sender-only issued list. The issued-only
          // view stays reachable via the in-page InboxNav switcher inside
          // conversations.tsx. Gated on payment_requests (Task 27, #1407) still — the
          // underlying issued/received tabs are the payment-rail-challenges surface.
          // App.tsx:607 — Guarded minAal="AAL2" (same route as the personal-context
          // Inbox item above).
          { icon: Inbox, label: "Inbox", href: "/challenges/conversations", flagKey: FLAG_KEYS.PAYMENT_REQUESTS, minAal: "AAL2" },
          { icon: Handshake, label: "Deals", href: "/deals", flagKey: FLAG_KEYS.DEALS_MONEY_OPS, minAal: "AAL2" },
          audit,
        ]
      : [security, connections, invitations, { icon: Key, label: VAULT_PAGE_TITLES.personal, href: "/vault-identifiers", minAal: "AAL2" }, challenges, audit];

  return items;
}

/**
 * Returns true when `location` should mark `item` as the active nav entry.
 * Shared by both navs so highlight rules stay identical.
 */
export function isNavItemActive(item: NavItem, location: string): boolean {
  if (item.matchPrefixes) {
    return item.matchPrefixes.some((p) => location.startsWith(p));
  }
  return location.startsWith(item.href) && item.href !== "#";
}

/**
 * Filters `items` down to the ones whose `flagKey` (if any) is enabled, and
 * attaches resolved attention counts to any item carrying a `badgeKey`.
 * Shared by sidebar.tsx + bottom-tabs.tsx (Reuse-First — both build their nav
 * from `items`, so this lives here once instead of twice). Calls
 * `useFeatureFlagWithDefault` for every pilot-core flag a nav item can carry
 * — a FIXED set, called unconditionally (Rules of Hooks) — defaulting ON so
 * the nav doesn't flicker hidden while flags are still loading.
 *
 * Badge resolution lives HERE rather than in each renderer (#2171) so desktop
 * and mobile can never show different counts, or one show a badge the other
 * doesn't — the same reason the item list itself was unified. The counts come
 * from a CONTEXT with a safe default, not a query, so this stays callable with
 * no QueryClient in scope: a nav rendered in isolation shows no badge rather
 * than throwing.
 */
export function useVisibleNavItems(items: NavItem[]): NavItem[] {
  const invitations = usePendingInvitationCounts();
  const badgeCounts: Record<NavBadgeKey, number> = { invitations: invitations.total };
  const commsHub = useFeatureFlagWithDefault(FLAG_KEYS.COMMS_HUB, true);
  const paymentRequests = useFeatureFlagWithDefault(FLAG_KEYS.PAYMENT_REQUESTS, true);
  const dealsMoneyOps = useFeatureFlagWithDefault(FLAG_KEYS.DEALS_MONEY_OPS, true);
  const bankAccounts = useFeatureFlagWithDefault(FLAG_KEYS.BANK_ACCOUNTS, true);
  const enabled: Partial<Record<FlagKey, boolean>> = {
    [FLAG_KEYS.COMMS_HUB]: commsHub,
    [FLAG_KEYS.PAYMENT_REQUESTS]: paymentRequests,
    [FLAG_KEYS.DEALS_MONEY_OPS]: dealsMoneyOps,
    [FLAG_KEYS.BANK_ACCOUNTS]: bankAccounts,
  };
  return items
    .filter((item) => !item.flagKey || enabled[item.flagKey])
    .map((item) =>
      item.badgeKey ? { ...item, badgeCount: badgeCounts[item.badgeKey] } : item,
    );
}
